/**
 * Carte admin « Diffuser en Reel Instagram » sur la page d'un exercice.
 *
 * Rend UN attempt de l'exercice en vidéo verticale (autoplay de la résolution,
 * via le pipeline YouTube Studio) et le publie en Instagram Reels — sans passer
 * par l'éditeur vidéo / la playlist. L'admin choisit la réponse à filmer, une
 * accroche (cible) et la légende, puis lance.
 */

import { useEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { cn } from '@jeraisonne/ui';
import {
  Instagram,
  Video,
  Film,
  Play,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  RotateCcw,
  ChevronDown,
} from 'lucide-react';
import { SUBJECTS, SCHOOL_LEVELS } from '@jeraisonne/db';
import type { SubjectSlug, SchoolLevel } from '@jeraisonne/db';
import { buildReelOverlay, buildReelsCaption, reelsTryUrl } from '@jeraisonne/db/youtube';
import { useAuth } from '@/lib/auth';
import { useSocialAccount, useYoutubeVideo } from '@/lib/admin/youtube-queries';
import { useStartSocialOAuth } from '@/lib/admin/use-social';
import { jobStepLabel, VIDEO_STATUS_LABEL } from '@/lib/admin/youtube-labels';
import { MusicPicker } from '@/features/youtube-studio/asset-pickers';
import {
  useExerciseCompletedAttempts,
  useCreateExerciseReel,
  usePublishExerciseReel,
  useReelPreviewUrl,
  type ExerciseReelPlatform,
} from './use-exercise-reel';

interface ExerciseReelCardProps {
  exerciseId: string;
  title: string;
  level: SchoolLevel | null;
  subject: SubjectSlug | null;
  /** Slug du chapitre — pour ouvrir le vrai player (`/learn/...`) et créer un attempt. */
  chapterSlug: string | null;
}

const fieldCls =
  'w-full rounded-xl border border-ink-200 bg-white px-3 py-2 font-sans text-sm text-ink-800 outline-none focus:border-primary-300';

/** Plateformes proposées + compte OAuth associé + provider de connexion. */
const REEL_PLATFORM_META: Record<
  ExerciseReelPlatform,
  {
    label: string;
    account: 'instagram' | 'tiktok';
    provider: 'meta' | 'tiktok';
    Icon: typeof Instagram;
  }
> = {
  instagram_reels: {
    label: 'Instagram Reels',
    account: 'instagram',
    provider: 'meta',
    Icon: Instagram,
  },
  tiktok: { label: 'TikTok', account: 'tiktok', provider: 'tiktok', Icon: Video },
};

export function ExerciseReelCard({
  exerciseId,
  title,
  level,
  subject,
  chapterSlug,
}: ExerciseReelCardProps) {
  const { user } = useAuth();
  const levelLabel = SCHOOL_LEVELS.find((l) => l.value === level)?.label ?? '';
  const subjectLabel = SUBJECTS.find((s) => s.value === subject)?.label ?? '';

  const ig = useSocialAccount('instagram');
  const tk = useSocialAccount('tiktok');
  const connected: Record<ExerciseReelPlatform, boolean> = {
    instagram_reels: !!ig.data,
    tiktok: !!tk.data,
  };
  const startOAuth = useStartSocialOAuth();

  const { data: attempts, isLoading: attemptsLoading } = useExerciseCompletedAttempts(exerciseId);
  const create = useCreateExerciseReel();

  const [attemptId, setAttemptId] = useState('');
  const [target, setTarget] = useState('');
  const [caption, setCaption] = useState('');
  const [musicAssetId, setMusicAssetId] = useState<string | null>(null);
  const [platforms, setPlatforms] = useState<ExerciseReelPlatform[]>(['instagram_reels']);
  const [createdVideoId, setCreatedVideoId] = useState<string | null>(null);
  // Bloc replié par défaut (admin only, prend de la place sur la page exercice).
  const [expanded, setExpanded] = useState(false);

  // Sélectionne la réponse la plus récente par défaut.
  useEffect(() => {
    if (!attemptId && attempts && attempts.length > 0) setAttemptId(attempts[0]!.attempt_id);
  }, [attempts, attemptId]);

  // Légende auto (une fois), éditable ensuite.
  const captionSeeded = useRef(false);
  useEffect(() => {
    if (captionSeeded.current) return;
    captionSeeded.current = true;
    setCaption(
      buildReelsCaption({
        title,
        level: levelLabel,
        subject: subjectLabel,
        url: reelsTryUrl(exerciseId),
      }),
    );
  }, [title, levelLabel, subjectLabel, exerciseId]);

  const regenerateCaption = () =>
    setCaption(
      buildReelsCaption({
        title,
        target,
        level: levelLabel,
        subject: subjectLabel,
        url: reelsTryUrl(exerciseId),
      }),
    );

  const togglePlatform = (p: ExerciseReelPlatform) =>
    setPlatforms((cur) => (cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]));

  async function handlePublish() {
    if (!user || !attemptId || platforms.length === 0) return;
    const overlay = buildReelOverlay({
      title,
      level: levelLabel,
      subject: subjectLabel,
      target,
      exerciseId,
    });
    const id = await create.mutateAsync({
      exerciseId,
      attemptId,
      userId: user.id,
      overlay,
      caption,
      musicAssetId,
      platforms,
    });
    setCreatedVideoId(id);
  }

  const connect = async (provider: 'meta' | 'tiktok') => {
    const { url } = await startOAuth.mutateAsync(provider);
    window.location.href = url;
  };

  const noAttempts = !attemptsLoading && (attempts?.length ?? 0) === 0;
  const canPublish = !!user && !!attemptId && platforms.length > 0 && !create.isPending;
  const canPlay = !!(subject && level && chapterSlug);
  // Plateformes choisies mais non connectées (bloque la publication, pas l'aperçu).
  const missing = platforms.filter((p) => !connected[p]);

  return (
    <div className="border-ink-200 mb-6 rounded-2xl border bg-white p-5">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="flex min-h-[2.75rem] w-full items-center gap-2 text-left"
      >
        <Film className="text-primary-500 h-4 w-4 shrink-0" />
        <h2 className="font-display text-ink-900 text-base font-extrabold">
          Diffuser ce replay (Reel / TikTok)
        </h2>
        <ChevronDown
          className={cn(
            'text-ink-400 ml-auto h-5 w-5 shrink-0 transition-transform',
            expanded && 'rotate-180',
          )}
        />
      </button>

      {expanded && (
        <div className="mt-4">
          <p className="text-ink-500 mb-4 font-sans text-xs">
            Filme la résolution d'une réponse à cet exercice (vidéo verticale, autoplay) et
            publie-la en Reel / TikTok. Fond de marque, texte de présentation incrusté.
          </p>

          {/* Sélection des plateformes + statut de connexion */}
          <div className="mb-4 space-y-2">
            {(Object.keys(REEL_PLATFORM_META) as ExerciseReelPlatform[]).map((p) => {
              const meta = REEL_PLATFORM_META[p];
              const isOn = platforms.includes(p);
              const account = meta.account === 'instagram' ? ig : tk;
              return (
                <div
                  key={p}
                  className={cn(
                    'flex items-center gap-3 rounded-xl border p-3',
                    isOn ? 'border-primary-200 bg-primary-50' : 'border-ink-200 bg-white',
                  )}
                >
                  <input
                    type="checkbox"
                    checked={isOn}
                    onChange={() => togglePlatform(p)}
                    className="h-5 w-5 shrink-0"
                    aria-label={meta.label}
                  />
                  <meta.Icon className="text-ink-500 h-4 w-4 shrink-0" />
                  <span className="font-display text-ink-800 flex-1 text-sm font-bold">
                    {meta.label}
                  </span>
                  {account.isLoading ? (
                    <span className="text-ink-400 text-xs">…</span>
                  ) : connected[p] ? (
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700">
                      <CheckCircle2 className="h-3.5 w-3.5" />{' '}
                      {account.data?.channel_title ?? 'Connecté'}
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => void connect(meta.provider)}
                      disabled={startOAuth.isPending}
                      className="border-ink-200 text-ink-700 inline-flex h-8 shrink-0 items-center gap-1 rounded-lg border bg-white px-2.5 text-xs font-bold active:scale-[.98] disabled:opacity-50"
                    >
                      {startOAuth.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
                      Connecter
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {attemptsLoading ? (
            <p className="text-ink-400 font-sans text-xs">Recherche des réponses…</p>
          ) : noAttempts ? (
            <div className="border-ink-200 space-y-3 rounded-xl border border-dashed p-4 text-center">
              <p className="text-ink-500 font-sans text-xs">
                Aucune réponse complétée pour cet exercice. <strong>Joue-le toi-même</strong>{' '}
                (admin, sans paywall) pour créer une réponse, puis reviens générer le Reel.
              </p>
              <PlayExerciseLink
                subject={subject}
                level={level}
                chapterSlug={chapterSlug}
                exerciseId={exerciseId}
                variant="primary"
                label="Jouer l'exercice"
              />
              {!canPlay && (
                <p className="text-ink-400 font-sans text-xs">
                  (Chapitre introuvable — impossible d'ouvrir le player.)
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Réponse à filmer */}
              <label className="block">
                <span className="text-ink-600 mb-1 block font-sans text-xs font-bold">
                  Réponse à filmer
                </span>
                <select
                  className={fieldCls}
                  value={attemptId}
                  onChange={(e) => setAttemptId(e.target.value)}
                  disabled={attemptsLoading}
                >
                  {(attempts ?? []).map((a) => (
                    <option key={a.attempt_id} value={a.attempt_id}>
                      {a.student_name}
                      {a.completed_at
                        ? ` · ${new Date(a.completed_at).toLocaleDateString('fr-FR')}`
                        : ''}
                    </option>
                  ))}
                </select>
                {canPlay && (
                  <Link
                    to="/learn/$subject/$level/$chapter/$exercise"
                    params={{
                      subject: String(subject),
                      level: String(level),
                      chapter: chapterSlug!,
                      exercise: exerciseId,
                    }}
                    className="text-primary-600 mt-1.5 inline-flex items-center gap-1 font-sans text-[11px] font-bold"
                  >
                    <Play className="h-3 w-3" /> Rejouer pour créer une nouvelle réponse
                  </Link>
                )}
              </label>

              {/* Cible / accroche (incrustée + légende) */}
              <label className="block">
                <span className="text-ink-600 mb-1 block font-sans text-xs font-bold">
                  Cible / accroche (incrustée dans la vidéo)
                </span>
                <input
                  className={fieldCls}
                  value={target}
                  onChange={(e) => setTarget(e.target.value)}
                  placeholder="Pour les 4e qui révisent Pythagore"
                  maxLength={160}
                />
              </label>

              {/* Musique (optionnelle) */}
              <div>
                <span className="text-ink-600 mb-1 block font-sans text-xs font-bold">
                  Musique (optionnelle)
                </span>
                <MusicPicker selectedId={musicAssetId} onSelect={setMusicAssetId} />
              </div>

              {/* Légende du Reel */}
              <label className="block">
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-ink-600 font-sans text-xs font-bold">Légende du Reel</span>
                  <button
                    type="button"
                    onClick={regenerateCaption}
                    className="text-primary-600 inline-flex items-center gap-1 text-[11px] font-bold"
                  >
                    <RotateCcw className="h-3 w-3" /> Régénérer
                  </button>
                </div>
                <textarea
                  className={cn(fieldCls, 'min-h-32 resize-y text-[13px]')}
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  maxLength={2200}
                />
                <span className="text-ink-400 mt-1 block text-xs">{caption.length}/2200</span>
              </label>

              {create.error && (
                <p className="text-accent-600 font-sans text-sm">
                  {(create.error as Error).message}
                </p>
              )}

              {!createdVideoId && (
                <button
                  type="button"
                  onClick={() => void handlePublish()}
                  disabled={!canPublish}
                  className="bg-accent-400 font-display hover:bg-accent-500 inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl px-5 text-sm font-bold text-white shadow-md transition-all active:scale-[.98] disabled:opacity-50 sm:w-auto"
                >
                  {create.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Film className="h-4 w-4" />
                  )}
                  Générer l'aperçu
                </button>
              )}

              {platforms.length === 0 && (
                <p className="text-ink-400 font-sans text-xs">
                  ⚠️ Choisis au moins une plateforme.
                </p>
              )}
              {platforms.length > 0 && missing.length > 0 && (
                <p className="text-ink-400 font-sans text-xs">
                  ⚠️ Connecte {missing.map((p) => REEL_PLATFORM_META[p].label).join(', ')} pour
                  pouvoir publier après l'aperçu.
                </p>
              )}

              {createdVideoId && (
                <ReelReview
                  videoId={createdVideoId}
                  platforms={platforms}
                  missing={missing}
                  onRefaire={() => setCreatedVideoId(null)}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Bouton « Jouer pour de vrai » → ouvre le player réel (`/learn/...`) qui PERSISTE
 * un attempt (≠ « Tester » en preview). L'admin n'a pas de paywall (`isStudent`
 * faux). Une fois terminé, l'attempt complété alimente le Reel.
 */
function PlayExerciseLink({
  subject,
  level,
  chapterSlug,
  exerciseId,
  variant,
  label,
}: {
  subject: SubjectSlug | null;
  level: SchoolLevel | null;
  chapterSlug: string | null;
  exerciseId: string;
  variant: 'primary' | 'secondary';
  label: string;
}) {
  if (!subject || !level || !chapterSlug) return null;
  return (
    <Link
      to="/learn/$subject/$level/$chapter/$exercise"
      params={{
        subject: String(subject),
        level: String(level),
        chapter: chapterSlug,
        exercise: exerciseId,
      }}
      className={cn(
        'font-display inline-flex h-11 items-center justify-center gap-2 rounded-xl px-5 text-sm font-bold transition-all active:scale-[.98]',
        variant === 'primary'
          ? 'bg-primary-500 hover:bg-primary-600 text-white shadow-md'
          : 'border-ink-200 text-ink-700 border bg-white',
      )}
    >
      <Play className="h-4 w-4" /> {label}
    </Link>
  );
}

/**
 * Aperçu + validation manuelle : suit le rendu, montre la vidéo générée et permet
 * de PUBLIER (ou refaire) sur les plateformes choisies avant tout envoi.
 */
function ReelReview({
  videoId,
  platforms,
  missing,
  onRefaire,
}: {
  videoId: string;
  platforms: ExerciseReelPlatform[];
  missing: ExerciseReelPlatform[];
  onRefaire: () => void;
}) {
  const { data } = useYoutubeVideo(videoId, { live: true });
  const { data: previewUrl } = useReelPreviewUrl(videoId);
  const publish = usePublishExerciseReel();

  if (!data) return null;
  const { video, job, publications } = data;
  const targetPubs = publications.filter((p) => (platforms as string[]).includes(p.platform));
  const failed =
    video.status === 'failed' ||
    job?.status === 'failed' ||
    targetPubs.some((p) => p.status === 'failed');
  const published = targetPubs.length > 0 && targetPubs.every((p) => p.status === 'published');
  const publishing =
    !published && targetPubs.some((p) => p.status === 'queued' || p.status === 'uploading');
  const previewReady = !failed && !published && !publishing && !!previewUrl;

  // ── Publié ──
  if (published) {
    return (
      <div className="mt-2 space-y-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3">
        <span className="inline-flex items-center gap-1.5 text-sm font-bold text-emerald-800">
          <CheckCircle2 className="h-4 w-4" /> Enregistré
        </span>
        <div className="space-y-1.5">
          {targetPubs.map((p) => (
            <div key={p.platform} className="flex items-center gap-2 text-sm">
              <span className="font-medium text-emerald-800">
                {REEL_PLATFORM_META[p.platform as ExerciseReelPlatform]?.label ?? p.platform}
              </span>
              {p.external_url && (
                <a
                  href={p.external_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary-600 ml-auto inline-flex items-center gap-1 text-xs font-bold"
                >
                  <ExternalLink className="h-3.5 w-3.5" /> Voir
                </a>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Échec ──
  if (failed) {
    const err = targetPubs.find((p) => p.error)?.error ?? job?.error;
    return (
      <div className="border-accent-200 bg-accent-50 mt-2 space-y-2 rounded-xl border p-3">
        <span className="text-accent-700 inline-flex items-center gap-1.5 text-sm font-bold">
          <AlertCircle className="h-4 w-4" /> Échec
        </span>
        {err && <p className="text-accent-600 font-sans text-xs">{err}</p>}
        <button
          type="button"
          onClick={onRefaire}
          className="border-ink-200 text-ink-700 inline-flex h-9 items-center gap-1.5 rounded-lg border bg-white px-3 text-xs font-bold active:scale-[.98]"
        >
          <RotateCcw className="h-3.5 w-3.5" /> Refaire
        </button>
      </div>
    );
  }

  // ── Aperçu prêt : on montre la vidéo + validation manuelle ──
  if (previewReady) {
    const canPublish = missing.length === 0 && !publish.isPending;
    return (
      <div className="border-ink-200 mt-2 space-y-3 rounded-xl border bg-white p-3">
        <p className="font-display text-ink-900 text-sm font-bold">
          Aperçu — valide avant publication
        </p>
        <video
          src={previewUrl!}
          controls
          playsInline
          className="aspect-9/16 mx-auto w-full max-w-[240px] rounded-lg bg-black"
        />
        {publish.error && (
          <p className="text-accent-600 font-sans text-xs">{(publish.error as Error).message}</p>
        )}
        <div className="flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={() => void publish.mutateAsync({ videoId, platforms })}
            disabled={!canPublish}
            className="bg-accent-400 font-display hover:bg-accent-500 inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-xl px-4 text-sm font-bold text-white shadow-md transition-all active:scale-[.98] disabled:opacity-50"
          >
            {publish.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Film className="h-4 w-4" />
            )}
            Publier
          </button>
          <button
            type="button"
            onClick={onRefaire}
            className="border-ink-200 text-ink-700 inline-flex h-11 items-center justify-center gap-1.5 rounded-xl border bg-white px-4 text-sm font-bold active:scale-[.98]"
          >
            <RotateCcw className="h-4 w-4" /> Refaire
          </button>
        </div>
        {missing.length > 0 && (
          <p className="text-ink-400 font-sans text-xs">
            Connecte d'abord : {missing.map((p) => REEL_PLATFORM_META[p].label).join(', ')}.
          </p>
        )}
      </div>
    );
  }

  // ── En cours (rendu de l'aperçu, ou publication lancée) ──
  return (
    <div className="border-ink-200 mt-2 rounded-xl border bg-white p-3">
      <span className="text-ink-700 inline-flex items-center gap-2 text-sm font-bold">
        <Loader2 className="text-primary-500 h-4 w-4 animate-spin" />
        {publishing
          ? 'Publication…'
          : (jobStepLabel(job?.current_step) ??
            VIDEO_STATUS_LABEL[video.status] ??
            'Génération de l’aperçu…')}
      </span>
    </div>
  );
}
